import React from "react";
import classNames from "classnames";
import QRCode from "react-qr-code";

const PhoneIcon = ({ className = "w-6 h-6" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      x="7"
      y="2.5"
      width="10"
      height="19"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <circle cx="12" cy="18" r="0.9" fill="currentColor" />
  </svg>
);

const UploadArrowIcon = ({ className = "w-6 h-6" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 16V4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M7.5 8.5 12 4l4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 20h14"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const MonitorIcon = ({ className = "w-6 h-6" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="4"
      width="18"
      height="12"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M9 20h6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M12 16v4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7.5 4.5 13 10l-5.5 5.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Step = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-2 text-center text-neutral-500">
    <div className="flex h-6 items-center justify-center">{icon}</div>
    <p className="max-w-[9rem] text-xs leading-snug">{label}</p>
  </div>
);

const SelectionUploadQrPanel = ({
  handoff,
  status,
  isLoading,
  error,
  onRefresh,
  compact = false,
  onBack,
}) => {
  const titleSize = compact ? "text-2xl" : "text-3xl";
  const qrWrapper = compact ? "w-48 h-48 p-3" : "w-56 h-56 p-4";

  const statusLabel = (() => {
    if (error) return error;
    if (status === "expired")
      return "This QR code has expired. Generate a new one to keep going.";
    if (status === "converting")
      return "Preparing your PDF for print. This can take up to a minute.";
    if (isLoading) return "Generating your secure upload link...";
    return "Keep this window open while the photo uploads from your phone.";
  })();

  return (
    <div
      className={classNames(
        "overflow-hidden rounded-lg bg-white text-neutral-900",
        compact ? "px-6 py-4" : "px-10 py-10",
      )}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mb-3 text-sm font-medium text-neutral-500 transition hover:text-neutral-900"
        >
          &larr; Back
        </button>
      )}

      <div className="mx-auto flex max-w-3xl flex-col items-center">
        <h2
          className={classNames(
            "text-center font-semibold tracking-tight text-neutral-900",
            titleSize,
          )}
        >
          Upload from your phone
        </h2>
        <p className="mt-3 max-w-md text-center text-sm leading-relaxed text-neutral-500">
          Scan the QR code with your phone camera to upload a photo. Keep this
          window open while uploading.
        </p>

        <div className="mt-6 flex w-full justify-center">
          <div
            className={classNames(
              "rounded-lg bg-neutral-50 border border-neutral-200",
              qrWrapper,
            )}
          >
            {handoff?.mobileUrl && status !== "expired" ? (
              <QRCode
                value={handoff.mobileUrl}
                size={256}
                style={{ height: "100%", maxWidth: "100%", width: "100%" }}
                viewBox="0 0 256 256"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-neutral-400">
                {isLoading ? "Generating..." : "QR code unavailable"}
              </div>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-sm leading-relaxed text-neutral-500">
          {statusLabel}
        </p>

        {status === "expired" && (
          <button
            type="button"
            onClick={onRefresh}
            className="mt-4 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
          >
            Generate a new QR code
          </button>
        )}

        {!compact && (
          <div className="mt-8 w-full max-w-xl">
            <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-start gap-2">
              <Step
                icon={<PhoneIcon className="w-5 h-5" />}
                label="Scan the QR code with your phone"
              />
              <div className="flex justify-center pt-1">
                <ChevronIcon className="w-4 h-4" />
              </div>
              <Step
                icon={<UploadArrowIcon className="w-5 h-5" />}
                label="Select the photo you'd like to frame"
              />
              <div className="flex justify-center pt-1">
                <ChevronIcon className="w-4 h-4" />
              </div>
              <Step
                icon={<MonitorIcon className="w-5 h-5" />}
                label="Your photo appears on this screen"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectionUploadQrPanel;
